import {useDownBreakpoint} from "@hooks";
import {
  Button,
  Dialog,
  IconButton,
  Stack,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";
import {
  CalendarIcon,
  DateOrTimeView,
  DateView,
  DesktopDatePicker,
  DesktopDateTimePicker,
  MobileDatePicker,
  MobileDateTimePicker,
} from "@mui/x-date-pickers";
import {Dayjs} from "dayjs";
import {useState} from "react";
import {CancelIcon} from "../icons";
import {format} from "../../constants";

const DatePicker = ({
  label,
  value,
  max,
  min,
  time,
  views,
  format: formatProp = format.date.long,
  sx,
  onChange,
}: {
  label: string;
  value: Dayjs | null;
  format?: string;
  time?: boolean;
  views?: DateOrTimeView[];
  min?: Dayjs;
  max?: Dayjs;
  sx?: SxProps<Theme>;
  onChange: (value: Dayjs | null) => void;
}) => {
  const isDownSm = useDownBreakpoint("sm");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleChangeDate = (newValue: Dayjs | null) => {
    if (newValue === null) {
      setAnchorEl(null);
    }
  };

  const handleAcceptDate = (newValue: Dayjs | null) => {
    onChange(newValue);
    setAnchorEl(null);
  };

  const handleMenuClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(null);
  };

  const clear = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onChange(null);
    setAnchorEl(null);
  };

  if (isDownSm) {
    return (
      <>
        {time ? (
          <MobileDateTimePicker
            fixedWeekNumber={6}
            showDaysOutsideCurrentMonth
            views={views}
            defaultValue={value}
            minDateTime={min}
            maxDateTime={max}
            onAccept={handleAcceptDate}
            onChange={handleChangeDate}
            format={formatProp}
            sx={{
              ...sx,
              "& .MuiIconButton-root": {p: 0.5},
              "& .MuiPickersSectionList-root": {py: 0},
            }}
            slotProps={{
              field: {clearable: true},
              clearIcon: {sx: {fontSize: "1rem"}},
              openPickerIcon: {sx: {fontSize: "1rem"}},
            }}
          />
        ) : (
          <MobileDatePicker
            fixedWeekNumber={6}
            showDaysOutsideCurrentMonth
            views={views as DateView[]}
            defaultValue={value}
            minDate={min}
            maxDate={max}
            onAccept={handleAcceptDate}
            onChange={handleChangeDate}
            format={formatProp}
            sx={{
              ...sx,
              "& .MuiIconButton-root": {p: 0.5},
              "& .MuiPickersSectionList-root": {py: 0},
            }}
            slotProps={{
              field: {clearable: true},
              clearIcon: {sx: {fontSize: "1rem"}},
              openPickerIcon: {sx: {fontSize: "1rem"}},
            }}
          />
        )}
      </>
    );
  }

  return (
    <>
      {time ? (
        <DesktopDateTimePicker
          fixedWeekNumber={6}
          showDaysOutsideCurrentMonth
          views={views}
          defaultValue={value}
          minDateTime={min}
          maxDateTime={max}
          onAccept={handleAcceptDate}
          format={formatProp}
          sx={{
            ...sx,
            "& .MuiIconButton-root": {p: 0.5},
            "& .MuiPickersSectionList-root": {py: 0},
          }}
          slotProps={{
            field: {clearable: true},
            clearIcon: {sx: {fontSize: "1rem"}},
            openPickerIcon: {sx: {fontSize: "1rem"}},
          }}
        />
      ) : (
        <DesktopDatePicker
          fixedWeekNumber={6}
          showDaysOutsideCurrentMonth
          views={views as DateView[]}
          defaultValue={value}
          minDate={min}
          maxDate={max}
          onAccept={handleAcceptDate}
          format={formatProp}
          sx={{
            ...sx,
            "& .MuiIconButton-root": {p: 0.5},
            "& .MuiPickersSectionList-root": {py: 0},
          }}
          slotProps={{
            field: {clearable: true},
            clearIcon: {sx: {fontSize: "1rem"}},
            openPickerIcon: {sx: {fontSize: "1rem"}},
          }}
        />
      )}
    </>
  );
};

export {DatePicker};
